import { useContext } from 'react'
import { useParams } from 'react-router-dom'

import { ToastContext } from '@reveni-io/reveni-ui'

import SaveChangesButtons from '@/common/components/SaveChangesButtons/SaveChangesButtons'
import UnsavedChangesAlert from '@/common/components/UnsavedChangesAlert/UnsavedChangesAlert'
import { userPermissions } from '@/common/constants'
import AppContext from '@/common/context/app.context'
import useMerchantFeatures from '@/common/hooks/useMerchantFeatures/useMerchantFeatures'
import useRegions from '@/common/hooks/useRegions/useRegions'
import useReturnReasons from '@/common/hooks/useReturnReasons/useReturnReasons'
import useRuleFeatures from '@/common/hooks/useRuleFeatures/useRuleFeatures'
import useStoreCurrencySymbol from '@/common/hooks/useStoreCurrencySymbol/useStoreCurrencySymbol'
import useStoreProductCategories from '@/common/hooks/useStoreProductCategories/useStoreProductCategories'
import useStoreProductCollections from '@/common/hooks/useStoreProductCollections/useStoreProductCollections'
import useStoreProductTypes from '@/common/hooks/useStoreProductTypes/useStoreProductTypes'
import useUserPermissions from '@/common/hooks/useUserPermissions/useUserPermissions'
import { focusOnError } from '@/configuration/pages/ConfigurationPage/helpers'

import usePolicyRule from './hooks/usePolicyRule/usePolicyRule'
import PolicyRuleActionEditor from './PolicyRuleActionEditor/PolicyRuleActionEditor'
import PolicyRuleBasicInfo from './PolicyRuleBasicInfo/PolicyRuleBasicInfo'
import PolicyRuleConditionsEditor from './PolicyRuleConditionsEditor/PolicyRuleConditionsEditor'
import PolicyRuleDetailSkeleton from './PolicyRuleDetailSkeleton/PolicyRuleDetailSkeleton'
import { getDisabledActions, mapCurrencyOptions, validatePolicyRule } from './helpers'
import * as Styles from './styles'

const PolicyRuleDetailConfiguration = () => {
  const { id } = useParams()
  const { info } = useContext(AppContext)
  const { setToastInfo } = useContext(ToastContext)
  const { hasPermission } = useUserPermissions()

  const isEditable = hasPermission(userPermissions.editPolicyRules)
  const { productTypes } = useStoreProductTypes({ storeId: info.selectedStore.id, doInitialRequest: true })
  const { productCategories } = useStoreProductCategories({ storeId: info.selectedStore.id, doInitialRequest: true })
  const { productCollections } = useStoreProductCollections({ storeId: info.selectedStore.id, doInitialRequest: true })
  const { regions } = useRegions()
  const { disabledActions, disabledConditionFields, fetchingRuleFeatures } = useRuleFeatures({
    storeId: info.selectedStore.id,
  })
  const { merchantFeatures, fetchingMerchantFeatures } = useMerchantFeatures({
    merchantId: info?.id,
  })
  const { returnReasons } = useReturnReasons({
    returnPortalId: info?.selectedReturnPortal?.id,
    doInitialRequest: true,
    disabled: false,
  })
  const { currencies, storeCurrencyCode } = useStoreCurrencySymbol()

  const currencyOptions = mapCurrencyOptions(currencies)
  const supportedLanguages = info?.selectedStore?.supported_languages || ['en']
  const availableIssueRefundStatuses = merchantFeatures?.available_issue_refund_status
  const availableIssueExchangeStatuses = merchantFeatures?.available_issue_exchange_status

  const {
    policyRule,
    errors,
    setErrors,
    hasChanges,
    revertChanges,
    onChangePolicyRule,
    onChangeActionConfig,
    saveChanges,
    fetchingPolicyRule,
    creatingPolicyRule,
    editingPolicyRule,
  } = usePolicyRule({
    id,
    storeId: info.selectedStore.id,
    setToastInfo,
    storeCurrencyCode,
    timeZone: info.selectedStore.timezone,
  })

  const onSave = () => {
    const formErrors = validatePolicyRule(policyRule, supportedLanguages)

    if (!formErrors.length) {
      saveChanges(policyRule)
    } else {
      focusOnError({ [formErrors[0].type]: formErrors[0].type })
      setErrors(formErrors)
    }
  }

  if (fetchingPolicyRule || fetchingRuleFeatures || fetchingMerchantFeatures) {
    return <PolicyRuleDetailSkeleton />
  }

  return (
    <Styles.Container>
      {hasChanges && <UnsavedChangesAlert />}
      <PolicyRuleBasicInfo
        name={policyRule.name}
        notes={policyRule.notes}
        validFrom={policyRule.valid_from}
        validUntil={policyRule.valid_until}
        onChange={onChangePolicyRule}
        errors={errors}
        disabled={!isEditable}
      />
      <PolicyRuleActionEditor
        actionType={policyRule.action_type}
        actionConfig={policyRule.action_config}
        onChange={onChangePolicyRule}
        onChangeConfig={onChangeActionConfig}
        disabled={!isEditable}
        currencyOptions={currencyOptions}
        currencies={currencies}
        storeCurrencyCode={storeCurrencyCode}
        productTypes={productTypes}
        productCategories={productCategories}
        productCollections={productCollections}
        errors={errors}
        disabledActions={getDisabledActions(
          disabledActions,
          availableIssueRefundStatuses,
          availableIssueExchangeStatuses
        )}
        availableIssueRefundStatuses={availableIssueRefundStatuses}
        availableIssueExchangeStatuses={availableIssueExchangeStatuses}
      />
      <PolicyRuleConditionsEditor
        conditions={policyRule.conditions || []}
        onChange={onChangePolicyRule}
        disabled={!isEditable}
        productTypes={productTypes}
        productCategories={productCategories}
        productCollections={productCollections}
        returnReasons={returnReasons}
        regions={regions}
        errors={errors}
        currencies={currencies}
        storeCurrencyCode={storeCurrencyCode}
        disabledConditionFields={disabledConditionFields}
      />
      <SaveChangesButtons
        loading={creatingPolicyRule || editingPolicyRule}
        disabled={creatingPolicyRule || editingPolicyRule || !isEditable || !policyRule.conditions?.length}
        onSave={onSave}
        hasChanges={isEditable && hasChanges}
        onRevertChanges={revertChanges}
      />
    </Styles.Container>
  )
}

export default PolicyRuleDetailConfiguration
